import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import EntryHistoryTable from '../../common/EntryHistoryTable';
import './ViewerDashboard.css';
import { API_BASE_URL } from '../../../config';
import axios from 'axios';

const ViewerDashboard = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user'));
  const [inwardEntries, setInwardEntries] = useState([]);
  const [outwardEntries, setOutwardEntries] = useState([]);
  const [activeTab, setActiveTab] = useState('inward');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let didCancel = false;
    const fetchWithTimeout = (url, options, timeout = 7000) => {
      return Promise.race([
        fetch(url, options).catch(err => { throw err; }), // ensure fetch errors are caught
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timed out')), timeout)
        )
      ]);
    };

    const fetchEntries = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('token');
        
        // Fetch inward entries
        const inwardResponse = await fetchWithTimeout(`${API_BASE_URL}/api/inward-entries`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!inwardResponse.ok) {
          throw new Error(`Inward entries fetch failed: ${inwardResponse.status}`);
        }

        const inwardData = await inwardResponse.json();
        if (!didCancel) setInwardEntries(inwardData);

        // Fetch outward entries
        const outwardResponse = await fetchWithTimeout(`${API_BASE_URL}/api/outward-entries`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!outwardResponse.ok) {
          throw new Error(`Outward entries fetch failed: ${outwardResponse.status}`);
        }

        const outwardData = await outwardResponse.json();
        if (!didCancel) setOutwardEntries(outwardData);
        
      } catch (error) {
        if (!didCancel) {
          setError(error.message === 'Request timed out' ? 'Server is taking too long to respond. Please try again later.' : error.message);
        }
      } finally {
        if (!didCancel) setLoading(false);
      }
    };

    fetchEntries();
    return () => { didCancel = true; };
    // eslint-disable-next-line
  }, []);

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';

    let headers = Array.from(new Set(
      data.reduce((acc, obj) => [...acc, ...Object.keys(obj)], [])
    ));
    // Remove bill_number and bill_amount if present
    headers = headers.filter(h => h !== 'bill_number' && h !== 'bill_amount');
    // Remove Bill Details column if present
    headers = headers.filter(h => h !== 'Bill Details');

    const csvRows = [];
    csvRows.push(headers.join(','));

    for (const row of data) {
      const values = headers.map(header => {
        let value = row[header] || '';
        if (header === 'Bill Details') {
          if (Array.isArray(row.bills) && row.bills.length > 0) {
            value = row.bills.map(b => `${b.bill_number || ''} (${b.bill_amount || ''})`).join('; ');
          } else {
            value = '';
          }
        }
        if ((header === 'Materials' || header === 'materials') && Array.isArray(row.materials_list)) {
          value = row.materials_list
            .map(m => `${m.name || ''} (${m.quantity || ''} ${m.uom || ''})`)
            .join('; ');
        }
        if (header === 'Date' && row.entry_date) {
          value = new Date(row.entry_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' });
        }
        if ((header === 'outdate' || header === 'out_date') && value) {
          if (typeof value === 'string' && value.length >= 10) {
            value = value.slice(0, 10);
          } else if (value instanceof Date) {
            value = value.toISOString().slice(0, 10);
          } else if (value) {
            try {
              value = new Date(value).toISOString().slice(0, 10);
            } catch {}
          }
        }
        if (typeof value === 'string') {
          value = value.replace(/"/g, '""');
          if (value.includes(',') || value.includes('\n') || value.includes('"')) {
            value = `"${value}"`;
          }
        }
        return value;
      });
      csvRows.push(values.join(','));
    }

    return csvRows.join('\n');
  };

  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' }); // Add BOM for Excel
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExportInward = async () => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/export/inward-entries`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (!response.data || !response.data.data || response.data.data.length === 0) {
        setError('No data available to export');
        return;
      }

      const csvContent = convertToCSV(response.data.data);
      const filename = `inward_entries_${new Date().toISOString().split('T')[0]}.csv`;
      downloadCSV(csvContent, filename);
    } catch (error) {
      console.error('Export error:', error);
      setError(error.response?.data?.message || 'Error exporting data. Please try again.');
    }
  };

  const handleExportOutward = async () => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/export/outward-entries`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (!response.data || !response.data.data || response.data.data.length === 0) {
        setError('No data available to export');
        return;
      }

      const csvContent = convertToCSV(response.data.data);
      const filename = `outward_entries_${new Date().toISOString().split('T')[0]}.csv`;
      downloadCSV(csvContent, filename);
    } catch (error) {
      console.error('Export error:', error);
      setError(error.response?.data?.message || 'Error exporting data. Please try again.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  if (!user || user.role !== 'Viewer') {
    navigate('/login');
    return null;
  }

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-message">Loading entries...</div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <nav className="dashboard-nav">
        <h2>Viewer Dashboard</h2>
        <div className="user-info">
          <span>Welcome, {user?.name}</span>
          <button onClick={handleLogout}>Logout</button>
        </div>
      </nav>

      <div className="dashboard-content">
        <div className="tabs">
          <button 
            className={`tab-button ${activeTab === 'inward' ? 'active' : ''}`}
            onClick={() => setActiveTab('inward')}
          >
            Inward Entries
          </button>
          <button 
            className={`tab-button ${activeTab === 'outward' ? 'active' : ''}`}
            onClick={() => setActiveTab('outward')}
          >
            Outward Entries
          </button>
        </div>

        <div className="table-container">
          {error && (
            <div className="error-message">
              Error: {error}
            </div>
          )}
          
          {loading ? (
            <div className="loading-message">Loading entries...</div>
          ) : (
            <>
              <div className="section-header">
                <button 
                  className="action-button"
                  onClick={activeTab === 'inward' ? handleExportInward : handleExportOutward}
                >
                  <i className="fas fa-download"></i> Export to CSV
                </button>
              </div>
              {activeTab === 'inward' ? (
                <EntryHistoryTable 
                  entries={inwardEntries} 
                  type="inward"
                  showActions={false} 
                  readOnly={true}
                  showCreatedBy={true} 
                  showReferenceNumber={inwardEntries.some(e => e.entry_type === 'Challan')}
                  showPartyName={true}
                  showPONumber={true}
                />
              ) : (
                <EntryHistoryTable 
                  entries={outwardEntries} 
                  type="outward"
                  showActions={false} 
                  readOnly={true}
                  showCreatedBy={true} 
                  showPartyName={true}
                  showPONumber={true}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewerDashboard;
